import { useEffect, useState } from 'react'

import { useSignMessage } from 'wagmi'

import { apiBindReferralCodeToWallet, apiGetBindMessageToSign } from '@src/api/point.api'
import LoadingIcon from '@src/components/icons/Loading'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { analyticsService } from '@src/services/analytics'
import { useApplicationStore } from '@src/zustand_stores'

const ReferralCodeInput = () => {
    const [code, setCode] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [error, setError] = useState('')
    const [isValid, setIsValid] = useState<boolean | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isApplied, setIsApplied] = useState(false)
    const { evmWalletAddress } = useWalletConnections()
    const boundReferralCode = useApplicationStore(state => state.boundReferralCode)
    const { signMessageAsync } = useSignMessage()

    // Calculate the boost percentage based on the referral code
    const getBoostPercentage = (code: string) => {
        return code ? 15 : 0
    }

    const handleCodeChange = (value: string) => {
        setCode(value.toUpperCase())
        setIsApplied(false)

        if (value && (value.length < 3 || value.length > 6)) {
            setIsValid(false)
        } else {
            setIsValid(null)
        }
    }

    useEffect(() => {
        let timeout: number
        if (successMessage) {
            timeout = window.setTimeout(() => {
                setSuccessMessage('')
                setIsApplied(true)
            }, 3000)
        }
        return () => clearTimeout(timeout)
    }, [successMessage])

    useEffect(() => {
        if (boundReferralCode?.code) {
            setCode(boundReferralCode.code)
            setIsValid(true)
            setIsApplied(true)
        }
    }, [boundReferralCode])

    // don't show if there's no connected wallet
    // this is temporary
    if (!evmWalletAddress) {
        return null
    }

    const handleApply = async () => {
        setIsLoading(true)
        const date = new Date()
        const timestamp = Math.floor(date.getTime() / 1000)

        try {
            const messageToSign = await apiGetBindMessageToSign(code, evmWalletAddress, timestamp)
            const signature = await signMessageAsync({ message: messageToSign })
            const response = await apiBindReferralCodeToWallet(code, timestamp, evmWalletAddress, signature)

            if (response.status !== 'success') {
                throw new Error(response.message)
            }

            setIsValid(true)
            setIsApplied(true)
            setSuccessMessage('Referral code successfully applied')

            // Track successful referral code entry
            analyticsService.trackSuccessfulReferral(code, evmWalletAddress)
        } catch (error) {
            setIsValid(false)
            setIsApplied(false)
            console.error(error)
            const errorMessage = error instanceof Error ? error.message : 'Something went wrong, try again later'
            setError(errorMessage)

            // Track incorrect referral code entry
            analyticsService.trackIncorrectReferral(code, evmWalletAddress, errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    // Note: Code removal functionality has been removed as requested

    return (
        <div className='referral-container space-y-2'>
            <div className='text-gray-300'>
                <p className='referral-label text-gray-500'>Referral Code</p>
                <div className='mt-2 space-y-2'>
                    {boundReferralCode?.code || isApplied ? (
                        <div className='referral-applied-container rounded-xl bg-[#1A1B1E] px-4 py-3'>
                            {(() => {
                                const boostPercentage = getBoostPercentage(boundReferralCode?.code || code)
                                return (
                                    <div className='flex items-center justify-between'>
                                        <span>Referral code '{code ?? boundReferralCode?.code}' applied</span>
                                        {boostPercentage > 0 && (
                                            <span className='referral-boost text-mach-green'>
                                                +{boostPercentage}% miles boost
                                            </span>
                                        )}
                                    </div>
                                )
                            })()}
                        </div>
                    ) : (
                        <div className='space-y-2'>
                            <div className='flex gap-2'>
                                <input
                                    type='text'
                                    value={code}
                                    onChange={e => handleCodeChange(e.target.value)}
                                    placeholder='Enter 5-character code'
                                    maxLength={6}
                                    className='referral-input flex-1 rounded-xl bg-[#1A1B1E] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50'
                                />
                                <button
                                    onClick={handleApply}
                                    disabled={isValid === false || isLoading || isApplied}
                                    className='referral-button rounded-xl bg-[#2A2B2E] px-6 py-3 transition-colors hover:bg-[#3A3B3E]'
                                >
                                    {isLoading ? (
                                        <LoadingIcon className='h-5 w-5 animate-spin' />
                                    ) : isApplied ? (
                                        'Applied'
                                    ) : (
                                        'Apply'
                                    )}
                                </button>
                            </div>
                            {error && <p className='text-xs text-red-500'>{error}</p>}

                            {successMessage && (
                                <p className='text-sm text-green-500 transition-opacity duration-300'>
                                    {successMessage}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ReferralCodeInput
